// services/runService.ts
import { Tenant, UserProfile, Module, Grade, Subject, Board } from '../types';
import { countPdfPages } from '../utils/pdfUtils';
import { auth } from '../firebase/config';

type GenerateWorksheetOptions = Record<string, any>;
type GenerateAssessmentOptions = Record<string, any>;

type ATypicalInputs = {
  grade: string;
  subject: string;
  files: File[];
};

type ShapeShifterInputs = {
  board: string;            // or Board if you import Board from types
  grade: Grade;
  subject: Subject;
  chapterFile: File;
  learningOutcomesText: string;
  numPeriods: string;
  periodDuration: string;
};

type PracticeMakesPermanentInputs = {
  mode: GenerateWorksheetOptions['mode'];
  grade: GenerateWorksheetOptions['grade'];
  subject: GenerateWorksheetOptions['subject'];
  chapterTitle: GenerateWorksheetOptions['chapterTitle'];
  chapterFile: File;
  densityLevel?: GenerateWorksheetOptions['densityLevel'];
  diagnosticFile?: File;
};

type TheAssessorInputs = GenerateAssessmentOptions;

type ModuleInputs =
  | ATypicalInputs
  | ShapeShifterInputs
  | PracticeMakesPermanentInputs
  | TheAssessorInputs;

const RUNMODULE_API_BASE = String(import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, '');
if (!RUNMODULE_API_BASE) {
  throw new Error('Missing VITE_API_BASE_URL. It must point to runmodule-api.');
}
const SYNTH_API_URL = `${RUNMODULE_API_BASE}/synthesizeAtypical`;
const RUNMODULE_API_URL = `${RUNMODULE_API_BASE}/runModule`;

async function authedGet(path: string): Promise<any> {
  const user = auth.currentUser;
  if (!user) throw new Error('not_signed_in');

  const token = await user.getIdToken(true);

  const r = await fetch(`${RUNMODULE_API_BASE}${path}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const j = await r.json().catch(() => ({}));
  if (!r.ok) {
    throw new Error(String((j as any)?.error || (j as any)?.message || `http_${r.status}`));
  }
  return j;
}
function getBillingForUser(userProfile: UserProfile): { type: 'TENANT' | 'PARTNER' } {
  const role = String((userProfile as any)?.role || '').toUpperCase().trim();
  if (role === 'PARTNER_ADMIN') return { type: 'PARTNER' };
  return { type: 'TENANT' };
}
// Deterministic: every backend call either has a fresh Bearer token or fails with a stable error payload
async function getJsonAuthHeaders(): Promise<Record<string, string>> {
  const u = auth.currentUser;
  if (!u) throw new Error(JSON.stringify({ error: 'not_signed_in' }));
  const token = await u.getIdToken(true);
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}
const serializeFile = async (file: File) => {
  return new Promise<{ name: string; type: string; content: string }>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1] || '';
      resolve({
        name: file.name,
        type: file.type || 'application/octet-stream',
        content: base64,
      });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const estimatePagesForFile = async (file: File): Promise<number> => {
  const t = String(file?.type || '');
  if (t === 'application/pdf') return await countPdfPages(file);
  if (t.startsWith('image/')) return 1;
  if (t === 'text/plain') return 1;
  return 1;
};

const estimateInputPages = async (module: Module, inputs: any): Promise<number> => {
  if (module === 'atypical' && Array.isArray(inputs?.files)) {
    let total = 0;
    for (const f of inputs.files as File[]) total += await estimatePagesForFile(f);
    return total;
  }

  if (module === 'shapeshifter' && inputs?.chapterFile) {
    return await estimatePagesForFile(inputs.chapterFile as File);
  }

  if (module === 'practice' && inputs?.chapterFile) {
    let total = await estimatePagesForFile(inputs.chapterFile as File);
    if (inputs?.diagnosticFile) total += await estimatePagesForFile(inputs.diagnosticFile as File);
    return total;
  }

  if (module === 'assessor' && Array.isArray(inputs?.inputFiles)) {
    let total = 0;
    for (const f of inputs.inputFiles as File[]) total += await estimatePagesForFile(f);
    return total;
  }

  return 0;
};

export const runService = {

  async startRun(tenant: Tenant, userProfile: UserProfile, module: Module, inputs: ModuleInputs) {
    const serializedInputs: any = { ...inputs };

    // Preserve explicit inputPages if provided by caller (do not overwrite with estimator)
    if (typeof (inputs as any)?.inputPages === 'number') {
      serializedInputs.inputPages = Number((inputs as any).inputPages || 0);
    } else {
      serializedInputs.inputPages = await estimateInputPages(module, inputs);
    }

    // A-Typical: transport-level batching (max 5 images per request) to avoid 413
    if (module === 'atypical' && Array.isArray((inputs as ATypicalInputs).files) && (inputs as ATypicalInputs).files.length > 0) {
      const files = (inputs as ATypicalInputs).files;

      const batches: File[][] = [];
      for (let i = 0; i < files.length; i += 5) batches.push(files.slice(i, i + 5));

      const extractionOutputs: string[] = [];
      let lastRunResponse: any = null;

      const inputPages =
        typeof serializedInputs.inputPages === 'number' ? serializedInputs.inputPages : 0;

      for (let i = 0; i < batches.length; i++) {
        const batchFiles = await Promise.all(batches[i].map(serializeFile));

       const headers = await getJsonAuthHeaders();

const res = await fetch(RUNMODULE_API_URL, {
  method: 'POST',
  headers,
 body: JSON.stringify({
  module,
  inputs: {
    ...(inputs as any),
    inputPages,
    files: batchFiles,
    batchIndex: i + 1,
    batchCount: batches.length,
  },
  billing: getBillingForUser(userProfile),
  tenantId: tenant.id,
  userId: userProfile.uid,
}),
});

        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(text || `RunModule API failed (${res.status})`);
        }

        const data = await res.json();
        lastRunResponse = data;

        const rawText = String(data?.aiResult?.resultText || '').trim();
        if (rawText) extractionOutputs.push(rawText);
      }

      // Call synthesis once
      const headers = await getJsonAuthHeaders();

const synthRes = await fetch(SYNTH_API_URL, {
  method: 'POST',
  headers,
  body: JSON.stringify({
  tenantId: tenant.id,
  grade: String((inputs as ATypicalInputs).grade || ''),
  subject: String((inputs as ATypicalInputs).subject || ''),
  batchNotes: extractionOutputs,
  inputPages, // <-- add this
}),
});

      if (!synthRes.ok) {
        const text = await synthRes.text().catch(() => '');
        throw new Error(text || `Synthesis API failed (${synthRes.status})`);
      }

      const synthData = await synthRes.json();

      const base = lastRunResponse || { aiResult: {} };
      base.aiResult = base.aiResult || {};
      base.aiResult.resultText = String(synthData?.aiResult?.resultText || '').trim();

      return base;
    }

    if (module === 'shapeshifter') {
  const s = inputs as ShapeShifterInputs;

  // deterministic: board must be supplied by UI
  const b = String((s as any)?.board || '').trim();
  if (!b) throw new Error('shapeshifter_missing_board');

  if (s.chapterFile) {
    serializedInputs.chapterFile = await serializeFile(s.chapterFile);
  }
}

    if (module === 'practice') {
      const pmp = inputs as PracticeMakesPermanentInputs;
      serializedInputs.chapterFile = await serializeFile(pmp.chapterFile);
      if (pmp.diagnosticFile) serializedInputs.diagnosticFile = await serializeFile(pmp.diagnosticFile);
    }

    if (module === 'assessor' && Array.isArray((inputs as any).inputFiles)) {
      serializedInputs.inputFiles = await Promise.all(
        ((inputs as any).inputFiles as File[]).map(serializeFile)
      );
    }

    const headers = await getJsonAuthHeaders();

const tenantIdToSend =
  module === 'lo_parse'
    ? String((tenant as any)?.id || 'platform')
    : String((tenant as any)?.id || '');

const response = await fetch(RUNMODULE_API_URL, {
  method: 'POST',
  headers,
  body: JSON.stringify({
    module,
    inputs: serializedInputs,
    billing: getBillingForUser(userProfile),
    tenantId: tenantIdToSend,
    userId: userProfile.uid,
  }),
});    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(text || 'RunModule API failed');
    }

    return response.json();
  },
  // ------------------------
  // Admin endpoints (PLATFORM_ADMIN only)
  // ------------------------
  async adminGetPartnerUsers(partnerId: string) {
    const pid = String(partnerId || '').trim();
    if (!pid) throw new Error('missing_partnerId');
    return await authedGet(`/admin/partnerUsers?partnerId=${encodeURIComponent(pid)}`);
  },

  async adminGetPartnerInvites(partnerId: string) {
    const pid = String(partnerId || '').trim();
    if (!pid) throw new Error('missing_partnerId');
    return await authedGet(`/admin/partnerInvites?partnerId=${encodeURIComponent(pid)}`);
  },

  async adminSetUserStatus(payload: { uid: string; status: 'ACTIVE' | 'INACTIVE' }) {
    const headers = await getJsonAuthHeaders();
    const res = await fetch(`${RUNMODULE_API_BASE}/admin/setUserStatus`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        uid: String(payload?.uid || '').trim(),
        status: String(payload?.status || '').trim().toUpperCase(),
      }),
    });

    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(String((j as any)?.error || (j as any)?.message || `http_${res.status}`));
    return j;
  },
async adminUpdatePartnerConfig(payload: { partnerDocId: string; contactEmail?: string | null; status?: 'ACTIVE' | 'INACTIVE' }) {
  const partnerDocId = String(payload?.partnerDocId || '').trim();
  if (!partnerDocId) throw new Error('missing_partnerDocId');

  const headers = await getJsonAuthHeaders();
  const res = await fetch(`${RUNMODULE_API_BASE}/admin/updatePartnerConfig`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      partnerDocId,
      contactEmail: payload?.contactEmail == null ? null : String(payload.contactEmail || '').trim(),
      status: payload?.status ? String(payload.status).trim().toUpperCase() : undefined,
    }),
  });

  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(String((j as any)?.error || (j as any)?.message || `http_${res.status}`));
  return j;
},
  async adminRevokeInvite(payload: { inviteId: string }) {
    const headers = await getJsonAuthHeaders();
    const res = await fetch(`${RUNMODULE_API_BASE}/admin/revokeInvite`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        inviteId: String(payload?.inviteId || '').trim(),
      }),
    });

    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(String((j as any)?.error || (j as any)?.message || `http_${res.status}`));
    return j;
  },
};
